import { SendOutlined } from '@ant-design/icons';
import {
  Avatar, Button, Card, Empty, Input, Spin, Tag, Typography,
} from 'antd';
import rideSupportChatApi from 'api/rideSupportChatApi';
import KT01BaseLayout from 'general/components/BaseLayout/KT01BaseLayout';
import ToastHelper from 'general/helpers/ToastHelper';
import Utils from 'general/utils/Utils';
import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';

const { Text, Title } = Typography;
const POLL_MS = 5000;

const BORDER = '#DBDFF1';
const TEXT_PRIMARY = '#494968';
const TEXT_SECONDARY = '#72728B';
const TEXT_MUTED = '#7E8299';
const BRAND = '#30BD6F';
const BRAND_LIGHT = '#ECFDF3';

// "Trung tâm hỗ trợ" is one persistent thread per account, not per role —
// the same person can message in as a driver in one bubble and a customer
// in the next, so the role has to be read off each message's own
// senderContext, never assumed for the whole conversation/user.
const senderContextLabel = (senderContext) => {
  if (senderContext === 'DRIVER') return 'Tài xế';
  if (senderContext === 'CUSTOMER') return 'Khách hàng';
  return 'Người dùng';
};
const senderContextColor = (senderContext) => {
  if (senderContext === 'DRIVER') return 'blue';
  if (senderContext === 'CUSTOMER') return 'green';
  return 'default';
};
function SenderContextTag({ senderContext }) {
  return (
    <Tag color={senderContextColor(senderContext)} style={{ marginInlineEnd: 0 }}>
      {senderContextLabel(senderContext)}
    </Tag>
  );
}

const formatListTime = (value) => {
  if (!value) return '';
  const date = new Date(value);
  const now = new Date();
  const sameDay = date.toDateString() === now.toDateString();
  return sameDay
    ? date.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })
    : date.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit' });
};
const formatBubbleTime = (value) => (value ? new Date(value).toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' }) : '');

const isSameDay = (a, b) => a.toDateString() === b.toDateString();
const formatDaySeparator = (value) => {
  const date = new Date(value);
  const now = new Date();
  if (isSameDay(date, now)) return 'Hôm nay';
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (isSameDay(date, yesterday)) return 'Hôm qua';
  return date.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

function ConversationListItem({ conversation, active, onClick }) {
  const unread = conversation.unreadCount > 0;
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(event) => { if (event.key === 'Enter') onClick(); }}
      style={{
        display: 'flex',
        gap: 10,
        padding: '12px 16px',
        cursor: 'pointer',
        background: active ? BRAND_LIGHT : '#fff',
        borderBottom: `1px solid ${BORDER}`,
        borderLeft: active ? `3px solid ${BRAND}` : '3px solid transparent',
      }}
    >
      <Avatar
        src={conversation.user?.avatarPath ? Utils.getFullUrl(conversation.user.avatarPath) : null}
        style={{ background: BRAND_LIGHT, color: BRAND, fontWeight: 600, flexShrink: 0 }}
      >
        {(conversation.user?.name || '?').charAt(0).toUpperCase()}
      </Avatar>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="d-flex justify-content-between align-items-start">
          <Text
            ellipsis
            style={{ color: TEXT_PRIMARY, fontWeight: unread ? 700 : 600, fontSize: 14, maxWidth: 190 }}
          >
            {conversation.user?.name || 'Người dùng EVG'}
          </Text>
          <Text style={{ fontSize: 11, color: unread ? BRAND : TEXT_MUTED, fontWeight: unread ? 600 : 400, whiteSpace: 'nowrap' }}>
            {formatListTime(conversation.lastMessageAt)}
          </Text>
        </div>
        <div className="d-flex justify-content-between align-items-center">
          <Text
            ellipsis
            style={{ fontSize: 12.5, color: unread ? TEXT_PRIMARY : TEXT_SECONDARY, fontWeight: unread ? 500 : 400, maxWidth: 190 }}
          >
            {conversation.lastMessageText || 'Chưa có tin nhắn'}
          </Text>
          {unread && (
            <span style={{
              minWidth: 18,
              height: 18,
              borderRadius: 9,
              background: BRAND,
              color: '#fff',
              fontSize: 11,
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 5px',
              flexShrink: 0,
            }}
            >
              {conversation.unreadCount > 99 ? '99+' : conversation.unreadCount}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function ConversationThread({ conversation, onConversationUpdated }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [draft, setDraft] = useState('');
  const listEndRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const response = await rideSupportChatApi.getMessages(conversation.id, { pageSize: 50 });
      setMessages(response?.messages || []);
    } catch (error) {
      ToastHelper.showError(error?.response?.data?.message || 'Không thể tải tin nhắn');
    } finally {
      setLoading(false);
    }
  }, [conversation.id]);

  useEffect(() => {
    setLoading(true);
    load();
    rideSupportChatApi.markRead(conversation.id).catch(() => {});
    const timer = setInterval(load, POLL_MS);
    return () => clearInterval(timer);
  }, [conversation.id, load]);

  useEffect(() => {
    listEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const send = async () => {
    const text = draft.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      await rideSupportChatApi.sendMessage(conversation.id, text);
      setDraft('');
      await load();
      onConversationUpdated();
    } catch (error) {
      ToastHelper.showError(error?.response?.data?.message || 'Không thể gửi tin nhắn');
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', height: '100%', minWidth: 0,
    }}
    >
      <div style={{
        padding: '13px 20px', borderBottom: `1px solid ${BORDER}`, display: 'flex', alignItems: 'center', gap: 10,
      }}
      >
        <Avatar
          src={conversation.user?.avatarPath ? Utils.getFullUrl(conversation.user.avatarPath) : null}
          style={{ background: BRAND_LIGHT, color: BRAND, fontWeight: 600 }}
        >
          {(conversation.user?.name || '?').charAt(0).toUpperCase()}
        </Avatar>
        <div>
          <Text strong style={{ color: TEXT_PRIMARY, fontSize: 14 }}>{conversation.user?.name || 'Người dùng EVG'}</Text>
          <div style={{ fontSize: 12, color: TEXT_MUTED }}>
            {conversation.user?.email}
            {conversation.user?.phone ? ` · ${conversation.user.phone}` : ''}
          </div>
        </div>
      </div>
      <div style={{
        flex: 1, overflowY: 'auto', padding: 20, background: '#F9F9F9',
      }}
      >
        {loading ? (
          <div className="d-flex justify-content-center pt-5"><Spin /></div>
        ) : messages.length === 0 ? (
          <Empty description="Chưa có tin nhắn" style={{ marginTop: 40 }} />
        ) : (
          messages.map((message, index) => {
            const isMine = message.senderRole === 'ADMIN';
            const previous = messages[index - 1];
            const showDaySeparator = message.sentAt
              && (!previous?.sentAt || !isSameDay(new Date(message.sentAt), new Date(previous.sentAt)));
            return (
              <div key={message.id}>
                {showDaySeparator && (
                  <div style={{
                    textAlign: 'center', margin: '4px 0 16px',
                  }}
                  >
                    <Text style={{
                      fontSize: 11.5,
                      color: TEXT_MUTED,
                      background: '#EFEFF4',
                      padding: '3px 10px',
                      borderRadius: 10,
                    }}
                    >
                      {formatDaySeparator(message.sentAt)}
                    </Text>
                  </div>
                )}
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: isMine ? 'flex-end' : 'flex-start',
                    marginBottom: 12,
                  }}
                >
                  {/* The account can message in as either role, so each
                      bubble is labelled like a distinct sender identity —
                      "Khách hàng" and "Tài xế" read as two different
                      people in the thread, not a property of one person. */}
                  {!isMine && (
                    <div style={{ marginBottom: 3 }}>
                      <SenderContextTag senderContext={message.senderContext} />
                    </div>
                  )}
                  <div
                    style={{
                      maxWidth: '65%',
                      padding: '9px 13px',
                      borderRadius: 14,
                      background: isMine ? BRAND : '#fff',
                      border: isMine ? 'none' : `1px solid ${BORDER}`,
                    }}
                  >
                    <Text style={{ color: isMine ? '#fff' : TEXT_PRIMARY, fontSize: 14, whiteSpace: 'pre-wrap' }}>{message.text}</Text>
                    <div style={{ textAlign: 'right', marginTop: 5 }}>
                      <Text style={{
                        fontSize: 10.5,
                        color: isMine ? 'rgba(255,255,255,0.8)' : TEXT_MUTED,
                      }}
                      >
                        {isMine ? 'Admin · ' : ''}
                        {formatBubbleTime(message.sentAt)}
                      </Text>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={listEndRef} />
      </div>
      <div style={{
        padding: 14, borderTop: `1px solid ${BORDER}`, display: 'flex', gap: 8, alignItems: 'flex-end',
      }}
      >
        <Input.TextArea
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onPressEnter={(event) => {
            if (!event.shiftKey) { event.preventDefault(); send(); }
          }}
          placeholder="Nhập trả lời..."
          autoSize={{ minRows: 1, maxRows: 4 }}
        />
        <Button type="primary" style={{ backgroundColor: '#30BD6F', borderColor: '#30BD6F' }} icon={<SendOutlined />} loading={sending} onClick={send} />
      </div>
    </div>
  );
}

export default function RideSupportChat() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [query, setQuery] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await rideSupportChatApi.getConversations({ page: 1, pageSize: 50 });
      setItems(response?.items || []);
      setTotal(response?.total || 0);
    } catch (error) {
      ToastHelper.showError(error?.response?.data?.message || 'Không thể tải danh sách hội thoại hỗ trợ');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const timer = setInterval(load, POLL_MS);
    return () => clearInterval(timer);
  }, [load]);

  const visibleItems = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return items;
    return items.filter((item) => [item.user?.name, item.user?.email, item.user?.phone]
      .filter(Boolean)
      .some((field) => field.toLowerCase().includes(term)));
  }, [items, query]);

  const selected = items.find((item) => item.id === selectedId) || null;

  return (
    <KT01BaseLayout isFullScreen>
      <div className="container-fluid px-4 my-4">
        <Card styles={{ body: { padding: 0 } }}>
          <div style={{
            padding: '15px 20px 10px', borderBottom: `1px solid ${BORDER}`,
          }}
          >
            <Title level={4} className="mb-0" style={{ color: TEXT_PRIMARY }}>Hỗ trợ xe ghép</Title>
            <Text style={{ color: TEXT_SECONDARY, fontSize: 13 }}>
              Hội thoại hỗ trợ giữa khách hàng/tài xế và admin ·
              {' '}
              <Text strong style={{ color: BRAND }}>{total}</Text>
              {' '}
              cuộc
            </Text>
          </div>
          <div style={{ display: 'flex', height: 'calc(100vh - 260px)', minHeight: 480 }}>
            <div style={{
              width: 320, flexShrink: 0, borderRight: `1px solid ${BORDER}`, display: 'flex', flexDirection: 'column',
            }}
            >
              <div style={{ padding: 12, borderBottom: `1px solid ${BORDER}` }}>
                <Input.Search
                  allowClear
                  placeholder="Tìm theo tên, email, SĐT"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                />
              </div>
              <div style={{ flex: 1, overflowY: 'auto' }}>
                {loading && items.length === 0 ? (
                  <div className="d-flex justify-content-center pt-5"><Spin /></div>
                ) : visibleItems.length === 0 ? (
                  <Empty
                    description={query ? 'Không tìm thấy hội thoại phù hợp' : 'Chưa có hội thoại hỗ trợ nào'}
                    style={{ marginTop: 40 }}
                  />
                ) : (
                  visibleItems.map((conversation) => (
                    <ConversationListItem
                      key={conversation.id}
                      conversation={conversation}
                      active={conversation.id === selectedId}
                      onClick={() => setSelectedId(conversation.id)}
                    />
                  ))
                )}
              </div>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              {selected ? (
                <ConversationThread key={selected.id} conversation={selected} onConversationUpdated={load} />
              ) : (
                <div className="d-flex align-items-center justify-content-center h-100">
                  <Empty description="Chọn một hội thoại để xem và trả lời tin nhắn" />
                </div>
              )}
            </div>
          </div>
        </Card>
      </div>
    </KT01BaseLayout>
  );
}
